/*eslint no-unused-vars: off */
import React from 'react';
import { IProjectStore } from 'store/companies/companiesTypes';
import styled from 'styled-components';
import { connect, MapStateToProps } from 'react-redux';
import { convertDigitToProperFormat } from 'utils/number';
import { RootState } from 'store/store';
import APopover from 'components/APopover';
import { IUserOption } from 'components/UserSelector/UserSelector';
import Icon from 'components/Icon';
import { black, exactWhite, gray, lightGray } from 'utils/style/colors';
import {
  Status,
  StatusContainer,
  StatusInfo,
  StatusPercent,
} from '../TaskListView/CircleTaskStatus/styles';

import { IAssignmentHistory, ITask } from 'store/tasks/tasksType';
import { WithTranslation, withTranslation } from 'react-i18next';
import { IPermission } from 'store/entity/IPermission';
import AssigneeChipContent from './AssigneeChipContent';

interface IOwnProps {
  chip: IUserOption;
  onChangeProgress: any;
  canChangeProgress: any;
  permission?: IPermission;
  readonlyDescription?: any;
  step: number;
  task?: ITask;
  compact?: boolean;
  viewType?: undefined | 'circle';
  showFields?: boolean;
  disablePopover?: boolean;
  onSetAssingmentHistory?: (assingmentHistory: IAssignmentHistory[]) => void;
  enablePopover?: boolean;
  onSetAssigneeIdToShow?: (asId: number | null) => void;
}

interface IStateToProps {
  users: RootState['users']['map'];
  tasks?: RootState['tasks'];
  project?: IProjectStore;
  isAdmin: boolean;
}

type IComponentProps = IStateToProps & IOwnProps;

interface IComponentState {
  showPopover: boolean;
  valueOverwrite: { [key: string]: string };
  loading: boolean;
}

interface IChipProps {
  hasProgress: boolean;
  showPointer?: boolean;
  compact?: boolean;
  selected?: boolean;
  disabled?: boolean;
  size?: 'large' | 'small' | undefined;
}

export const AssigneeHeader = styled('label')`
  font-weight: 500;
  font-size: 15px;
  color: ${black};
`;
export const Chip = styled('div')<IChipProps>`
  width: ${(props) => (props.compact ? '8rem' : 'auto')};
  max-width: 100%;
  min-width: 8rem;
  height: 2rem;
  white-space: nowrap;
  display: inline-flex;
  font-size: ${(props) => (props.size === 'large' ? '1.2rem' : '0.8rem')};
  align-items: stretch;
  font-weight: ${(props) => (props.size === 'large' ? '300' : '500')};
  line-height: 2rem;
  color: ${(props) => (!props.disabled || props.selected ? black : exactWhite)};
  margin: 3px;
  cursor: ${(props) =>
    props.hasProgress && props.showPointer ? 'pointer' : 'default'};
  & > span {
    width: 100%;
    direction: ${(props) => props.theme.direction};
    margin-inline-end: ${(props) => (!props.hasProgress ? '0.5rem' : '0px')};
    padding: 0 0.5rem;
    word-break: break-word;
    ${(props) =>
      props.theme.direction === 'rtl'
        ? `border-top-left-radius: ${!props.hasProgress && '10px'};
    border-top-right-radius: 10px;
    border-bottom-left-radius: ${!props.hasProgress && '10px'};
    border-bottom-right-radius: 10px;`
        : `border-top-right-radius: ${!props.hasProgress && '10px'};
    border-top-left-radius: 10px;
    border-bottom-right-radius: ${!props.hasProgress && '10px'};
    border-bottom-left-radius: 10px;`}
    background-color: ${(props) =>
      props.selected ? '#c0c0c0' : `${gray}!important`};
    overflow: hidden;
  }
`;

interface IChipProgress {
  progress?: number;
}
export const ChipProgress = styled('div')<IChipProgress>`
  color: ${black};
  flex: none;
  width: 2.5rem;
  display: inline-flex;
  padding: 0 0.3rem;
  align-items: center;
  justify-content: flex-start;
  background-color: ${lightGray};
  overflow: hidden;
  ${(props) =>
    props.theme.direction === 'rtl'
      ? `border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;`
      : `border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;`}
  &:hover {
    overflow: visible;
  }
  & > i {
    width: 25px;
  }
`;

export const getProgressIcon = (val: number) => {
  if (val === 100) return 'check-circle';
  if (val === 0) return 'circle';
  if (val === -1) return 'x-circle';
  return 'activity';
};

const ASSIGNEE_CHIP_POPPER_MODIFIERS = {
  preventOverflow: {
    boundariesElement: 'viewport',
    padding: 8,
  },
  flip: {
    enabled: false,
  },
};

// Wait until TaskEditor.scrollToAssigneeSection finishes smooth scrolling.
const ENABLE_POPOVER_OPEN_DELAY_MS = 500;

class AssigneeChip extends React.PureComponent<
  IComponentProps & WithTranslation,
  IComponentState
> {
  state = {
    showPopover: false,
    valueOverwrite: {},
    loading: false,
  };

  private enablePopoverTimer: number | null = null;

  componentDidMount(): void {
    if (this.props.enablePopover) {
      this.scheduleEnablePopoverOpen();
    }
  }

  componentDidUpdate(
    prevProps: Readonly<IComponentProps & WithTranslation>
  ): void {
    if (prevProps.enablePopover !== this.props.enablePopover) {
      if (this.props.enablePopover) {
        this.scheduleEnablePopoverOpen();
      } else {
        this.clearEnablePopoverTimer();
        this.handleClosePopover();
      }
    }
  }

  componentWillUnmount(): void {
    this.clearEnablePopoverTimer();
  }

  clearEnablePopoverTimer = () => {
    if (this.enablePopoverTimer !== null) {
      window.clearTimeout(this.enablePopoverTimer);
      this.enablePopoverTimer = null;
    }
  };

  scheduleEnablePopoverOpen = () => {
    this.clearEnablePopoverTimer();
    this.enablePopoverTimer = window.setTimeout(() => {
      this.enablePopoverTimer = null;
      if (this.props.enablePopover) {
        this.handleShowPopover(true);
      }
    }, ENABLE_POPOVER_OPEN_DELAY_MS);
  };

  handleClosePopover = () => {
    this.setState({
      showPopover: false,
    });
    this.props.onSetAssigneeIdToShow?.(null);
  };

  handleShowPopover = (show: boolean) => {
    if (show) {
      this.setState({ showPopover: true });
    } else {
      this.handleClosePopover();
    }
  };

  getStatusTitle = (status: string, task: ITask) => {
    if (status === 'complete') {
      return task.assigneeCompleteTitle ?? this.props.t('complete');
    }
    if (status === 'started') {
      return task.assigneeStartedTitle ?? this.props.t('started');
    }
    if (status === 'notStarted') {
      return task.assigneeNotStartedTitle ?? this.props.t('notStarted');
    }
    return '';
  };

  render() {
    const {
      chip,
      canChangeProgress,
      task,
      compact,
      viewType,
      readonlyDescription,
      disablePopover = false,
      isAdmin,
    } = this.props;
    const { showPopover } = this.state;

    const isAssignment = typeof chip.progress === 'number';

    return (
      <APopover
        arrow={true}
        curveBorder={true}
        trigger="click"
        placement="auto"
        viewportFit={true}
        modifier={ASSIGNEE_CHIP_POPPER_MODIFIERS}
        show={showPopover}
        disabled={disablePopover || (!isAssignment && !!task)}
        onShowChange={this.handleShowPopover}
        target={
          <div data-assignee-user-id={chip.userId}>
            {task ? (
              viewType === 'circle' ? (
                <StatusContainer>
                  <Status role="button" complete={chip.status === 'complete'}>
                    {task.type === 'atOnce' ? (
                      <Icon type={getProgressIcon(chip.progress || 0)} />
                    ) : (
                      <StatusPercent>
                        {convertDigitToProperFormat(
                          (chip.progress || 0).toString() + '%'
                        )}
                      </StatusPercent>
                    )}
                  </Status>
                  {task.type === 'atOnce' && (
                    <StatusInfo className="visible-on-hover">
                      {this.getStatusTitle(chip.status || '', task)}
                    </StatusInfo>
                  )}
                </StatusContainer>
              ) : (
                <Chip
                  compact={compact}
                  key={(chip as IUserOption).userId}
                  hasProgress={typeof chip.progress === 'number'}
                  showPointer={
                    typeof chip.progress === 'number' && canChangeProgress
                  }
                  // onClick={this.handleShowPopover}
                >
                  <span>
                    {chip.name}
                    {chip.relatedRoleName && ' (' + chip.relatedRoleName + ')'}
                  </span>
                  {typeof chip.progress === 'number' && (
                    <ChipProgress progress={chip.progress}>
                      {task.type === 'progressive' &&
                        convertDigitToProperFormat(
                          chip.progress.toString() + this.props.t('percent')
                        )}
                      {task.type === 'atOnce' && (
                        <Icon type={getProgressIcon(chip.progress)} />
                      )}
                    </ChipProgress>
                  )}
                </Chip>
              )
            ) : (
              <div />
            )}
          </div>
        }
      >
        <div style={{ overflowX: 'auto' }}>
          <AssigneeChipContent
            chip={chip}
            canChangeProgress={canChangeProgress}
            onChangeProgress={this.props.onChangeProgress}
            readonlyDescription={readonlyDescription}
            task={task}
            isAdmin={isAdmin}
            onSetAssingmentHistory={this.props.onSetAssingmentHistory}
            project={this.props.project}
            showFields={this.props.showFields}
            showPopover={showPopover}
            // onShowPopover={this.handleShowPopover}
            onClosePopover={this.handleClosePopover}
          />
        </div>
      </APopover>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  let project = undefined;
  const users = state.users.map;

  if (props.showFields && props.task) {
    Object.values(state.companies.data || {}).forEach((cmp) =>
      Object.values(cmp.projects || {}).forEach((prj) => {
        if (prj.id === props.task?.projectId) {
          project = prj;
        }
      })
    );
  }

  const tasks = state.tasks;

  const isAdmin = Boolean(props.permission?.mark);

  return { users, tasks, project, isAdmin };
};

export default connect(mapStateToProps)(
  withTranslation('taskManager')(AssigneeChip)
);
